import type {
  Events,
  MetaDatum,
  PausaFinalizada,
  PausasActivas,
  PermisoDelDiaFinalizado,
  PermisosActivos,
} from '../interfaces/schedule.interface';
import type {
  ActionEventsEnvelopeV2,
  EventsPayloadV2,
  MetaDatumV2,
  PausaV2,
  PermisoV2,
  SessionV2,
  WorkerProfileV2,
} from '../interfaces/scheadule-v2.interface';

// Compat temporal para respuestas de acciones híbridas.
// Se borra cuando backend devuelva el mismo contrato canónico que GET canonical.
export interface ActionEventsRefreshPayload {
  canonicalEvents?: EventsPayloadV2;
  legacyEvents: Events;
}

export function adaptHybridActionEventsPayload(payload: EventsPayloadV2): Events {
  return {
    meta: adaptMeta(payload.meta),
    data: payload.data as unknown as Events['data'],
  };
}

export function adaptHybridActionEventsEnvelope(envelope: unknown): ActionEventsRefreshPayload | undefined {
  if (!isHybridActionEventsEnvelope(envelope)) return undefined;

  const canonicalEvents: EventsPayloadV2 = {
    session: buildSession(envelope),
    meta: normalizeMetaToCanonical(envelope.meta),
    data: envelope.data,
  };

  return {
    canonicalEvents,
    legacyEvents: adaptHybridActionEventsPayload(canonicalEvents),
  };
}

function adaptMeta(meta: MetaDatumV2): MetaDatum {
  return {
    jornada_anterior: meta.jornada_anterior as MetaDatum['jornada_anterior'],
    es_nocturno: meta.es_nocturno,
    evento_anterior_sin_fichaje: meta.evento_anterior_sin_fichaje,
    company_id: meta.company_id,
    name: meta.name,
    dni: meta.dni,
    horas_extras_activas: meta.horas_extras_activas as MetaDatum['horas_extras_activas'],
    permisos_del_dia_activos: meta.permisos_del_dia_activos ? adaptOpenPermission(meta.permisos_del_dia_activos) : null,
    pausas_activas: meta.pausas_activas ? adaptOpenPause(meta.pausas_activas) : null,
    permisos_del_dia_finalizados: (meta.permisos_del_dia_finalizados ?? []).map(adaptClosedPermission),
    pausas_finalizadas: (meta.pausas_finalizadas ?? []).map(adaptClosedPause),
  };
}

function normalizeMetaToCanonical(meta: MetaDatumV2 & { worker_profile?: WorkerProfileV2 }): MetaDatumV2 {
  return {
    ...meta,
    permisos_del_dia_activos: normalizeOpenPermission(meta.permisos_del_dia_activos),
    permisos_del_dia_finalizados: (meta.permisos_del_dia_finalizados ?? []).map(normalizeClosedPermission),
    pausas_activas: normalizeOpenPause(meta.pausas_activas),
    pausas_finalizadas: (meta.pausas_finalizadas ?? []).map(normalizeClosedPause),
  };
}

function adaptOpenPermission(permission: PermisoV2): PermisosActivos {
  return {
    id: String(permission.request_id || permission.id),
    shedule_id: String(permission.schedule_id),
    motivo_id: String(permission.reason.id),
    timeStart: toLegacyTime(permission.check_in_at ?? permission.starts_at),
    timeEnd: permission.check_out_at ? toLegacyTime(permission.check_out_at) : null,
    nombre: permission.reason.name,
    comments: permission.comment ?? '',
    selected_days: toLegacyDay(permission.starts_at),
  };
}

function adaptClosedPermission(permission: PermisoV2): PermisoDelDiaFinalizado {
  return {
    request_id: permission.request_id,
    id: permission.id,
    shedule_id: permission.schedule_id,
    company_id: permission.company_id,
    permiso_motivo_nombre: permission.reason.name,
    worker_id: permission.worker_id ?? 0,
    date_start: permission.starts_at,
    date_end: permission.ends_at ?? permission.starts_at,
    date_in: permission.check_in_at ?? permission.starts_at,
    date_out: permission.check_out_at ?? permission.ends_at ?? permission.starts_at,
    tiempo_real: permission.duration_minutes,
    comentario_permiso: permission.comment ?? '',
    imagen_permiso: toLegacyAttachment(permission.attachments.images),
    pdf_permiso: toLegacyAttachment(permission.attachments.pdfs),
  };
}

function adaptOpenPause(pause: PausaV2): PausasActivas {
  return {
    id: String(pause.id),
    shedule_id: String(pause.schedule_id),
    motivo_id: String(pause.reason.id),
    user_id: String(pause.worker_id),
    hora_entrada: pause.starts_at,
    hora_salida: null,
    motivo: pause.reason.name,
    comentario: pause.comment ?? '',
  };
}

function adaptClosedPause(pause: PausaV2): PausaFinalizada {
  return {
    pausa_id: String(pause.id),
    pausa_motivo_id: String(pause.reason.id),
    worker_id: String(pause.worker_id),
    pausa_motivo: pause.reason.name,
    shedule_id: pause.schedule_id,
    hora_entrada: pause.starts_at,
    hora_salida: pause.ends_at,
    segundos: pause.duration_seconds,
    comentario_pausa: pause.comment ?? '',
  };
}

function buildSession(envelope: ActionEventsEnvelopeV2): SessionV2 {
  const session = envelope.session ?? envelope.user_logged ?? {};
  const profile = session.worker_profile ?? envelope.meta.worker_profile ?? buildFallbackWorkerProfile(envelope.meta);

  return {
    id: session.id ?? profile.worker_id,
    company_id: session.company_id ?? profile.company_id ?? envelope.meta.company_id,
    menu_version: session.menu_version ?? 0,
    updated: session.updated ?? false,
    metadata_version: session.metadata_version ?? 0,
    worker_profile: profile,
    roles: session.roles,
    worker_config: session.worker_config,
    location_cache_at: session.location_cache_at,
    help_videos: session.help_videos,
  };
}

function normalizeOpenPermission(permission: unknown): PermisoV2 | null {
  if (!permission) return null;
  if (isPermisoV2(permission)) return permission;

  const legacy = permission as Partial<PermisosActivos>;
  if (!legacy.id || !legacy.shedule_id || !legacy.motivo_id || !legacy.selected_days || !legacy.timeStart) {
    return null;
  }

  return {
    id: Number.parseInt(legacy.id, 10),
    request_id: Number.parseInt(legacy.id, 10),
    schedule_id: Number.parseInt(legacy.shedule_id, 10),
    worker_id: null,
    company_id: 0,
    starts_at: `${legacy.selected_days} ${legacy.timeStart}`,
    ends_at: legacy.timeEnd ? `${legacy.selected_days} ${legacy.timeEnd}` : null,
    check_in_at: `${legacy.selected_days} ${legacy.timeStart}`,
    check_out_at: legacy.timeEnd ? `${legacy.selected_days} ${legacy.timeEnd}` : null,
    duration_minutes: 0,
    reason: {
      id: Number.parseInt(legacy.motivo_id, 10),
      name: legacy.nombre ?? '',
    },
    comment: legacy.comments ?? '',
    attachments: { images: 0, pdfs: 0 },
  };
}

function normalizeClosedPermission(permission: unknown): PermisoV2 {
  if (isPermisoV2(permission)) return permission;

  const legacy = permission as PermisoDelDiaFinalizado;
  return {
    id: legacy.id,
    request_id: legacy.request_id,
    schedule_id: legacy.shedule_id ?? 0,
    worker_id: legacy.worker_id,
    company_id: legacy.company_id,
    starts_at: legacy.date_start,
    ends_at: legacy.date_end,
    check_in_at: legacy.date_in,
    check_out_at: legacy.date_out,
    duration_minutes: legacy.tiempo_real,
    reason: {
      id: 0,
      name: legacy.permiso_motivo_nombre,
    },
    comment: legacy.comentario_permiso,
    attachments: {
      images: legacy.imagen_permiso && legacy.imagen_permiso !== '0' ? 1 : 0,
      pdfs: legacy.pdf_permiso && legacy.pdf_permiso !== '0' ? 1 : 0,
    },
  };
}

function normalizeOpenPause(pause: unknown): PausaV2 | null {
  if (!pause) return null;
  if (isPausaV2(pause)) return pause;

  const legacy = pause as Partial<PausasActivas>;
  if (!legacy.id || !legacy.shedule_id || !legacy.motivo_id || !legacy.user_id || !legacy.hora_entrada) {
    return null;
  }

  return {
    id: Number.parseInt(legacy.id, 10),
    schedule_id: Number.parseInt(legacy.shedule_id, 10),
    worker_id: Number.parseInt(legacy.user_id, 10),
    company_id: 0,
    starts_at: legacy.hora_entrada,
    ends_at: null,
    duration_seconds: 0,
    reason: {
      id: Number.parseInt(legacy.motivo_id, 10),
      name: legacy.motivo ?? '',
    },
    comment: legacy.comentario ?? '',
  };
}

function normalizeClosedPause(pause: unknown): PausaV2 {
  if (isPausaV2(pause)) return pause;

  const legacy = pause as PausaFinalizada;
  return {
    id: Number.parseInt(legacy.pausa_id, 10),
    schedule_id: legacy.shedule_id,
    worker_id: Number.parseInt(legacy.worker_id, 10),
    company_id: 0,
    starts_at: legacy.hora_entrada,
    ends_at: legacy.hora_salida,
    duration_seconds: legacy.segundos,
    reason: {
      id: Number.parseInt(legacy.pausa_motivo_id, 10),
      name: legacy.pausa_motivo,
    },
    comment: legacy.comentario_pausa,
  };
}

function buildFallbackWorkerProfile(meta: MetaDatumV2): WorkerProfileV2 {
  return {
    worker_id: 0,
    company_id: meta.company_id,
    name: meta.name,
    dni: meta.dni,
    short_name: '',
    include_location: false,
    pausas: false,
    pausa_limite: 0,
    pausa_computable: false,
    permisos_retribuidos_jornada: false,
    horas_extras_jornada: false,
    ajuste_fichaje_entrada: false,
    ajuste_fichaje: false,
    trabajoLibre: false,
    fichajeSinAgenda: false,
    teletrabajo_activo: false,
    teletrabajo_dia: false,
    modificar_permisos: false,
    mapa_requerido_fichar: false,
    firma_requerida: false,
    mostrar_estimadas: false,
    language: 'es',
    limite_comida: 0,
  };
}

function isHybridActionEventsEnvelope(value: unknown): value is ActionEventsEnvelopeV2 {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<ActionEventsEnvelopeV2>;

  if (!candidate.meta || !Array.isArray(candidate.data)) return false;

  return true;
}

function isPermisoV2(value: unknown): value is PermisoV2 {
  if (!value || typeof value !== 'object') return false;
  return 'schedule_id' in value && 'reason' in value && 'attachments' in value;
}

function isPausaV2(value: unknown): value is PausaV2 {
  if (!value || typeof value !== 'object') return false;
  return 'schedule_id' in value && 'reason' in value && 'duration_seconds' in value;
}

function toLegacyDay(dateTime: string): string {
  return dateTime.slice(0, 10);
}

function toLegacyTime(dateTime: string): string {
  const time = dateTime.split('T')[1] ?? dateTime.split(' ')[1] ?? '';
  return time || dateTime;
}

function toLegacyAttachment(count: number): string {
  return count > 0 ? String(count) : '0';
}
